// public-messaging.js - Sistema de mensajería para páginas públicas

class PublicMessaging {
    constructor() {
        this.modal = null;
        this.currentBusiness = null;
        this.isAuthenticated = false;
        this.init();
    }

    init() {
        this.createModal();
        this.checkAuthStatus();
        this.bindEvents();
    }

    // Verificar si el usuario está autenticado
    async checkAuthStatus() {
        try {
            const token = localStorage.getItem('supabase.auth.token') || 
                         sessionStorage.getItem('supabase.auth.token');
            
            if (token) {
                // Verificar token con el backend
                const apiBaseURL = window.apiConfig?.baseURL || 'http://localhost:3000/api/v1';
                const response = await fetch(`${apiBaseURL}/auth/verify`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                
                this.isAuthenticated = response.ok;
            }
        } catch (error) {
            console.log('Auth check failed:', error);
            this.isAuthenticated = false;
        }
    }

    // Crear el modal HTML
    createModal() {
        const existing = document.getElementById('public-message-modal');
        if (existing) {
            try { existing.remove(); } catch {}
        }

        const modal = document.createElement('div');
        modal.id = 'public-message-modal';
        modal.className = 'public-message-modal';

        const content = document.createElement('div');
        content.className = 'public-message-content';

        const header = document.createElement('div');
        header.className = 'public-message-header';

        const h3 = document.createElement('h3');
        h3.className = 'public-message-title';
        h3.textContent = 'Escribir mensaje';

        const closeBtn = document.createElement('button');
        closeBtn.className = 'public-message-close';
        closeBtn.type = 'button';
        const closeIcon = document.createElement('i');
        closeIcon.className = 'fas fa-times';
        closeBtn.appendChild(closeIcon);

        header.appendChild(h3);
        header.appendChild(closeBtn);

        const form = document.createElement('form');
        form.className = 'public-message-form';
        form.id = 'public-message-form';

        const bizInfo = document.createElement('div');
        bizInfo.className = 'public-business-info';
        bizInfo.id = 'business-info';

        const nameGroup = document.createElement('div');
        nameGroup.className = 'public-form-group';
        nameGroup.id = 'contact-name-group';
        const nameLabel = document.createElement('label');
        nameLabel.className = 'public-form-label';
        nameLabel.htmlFor = 'contact-name';
        nameLabel.textContent = 'Tu nombre';
        const nameInput = document.createElement('input');
        nameInput.type = 'text';
        nameInput.id = 'contact-name';
        nameInput.className = 'public-form-input';
        nameInput.placeholder = 'Tu nombre completo';
        nameInput.required = true;
        nameGroup.appendChild(nameLabel);
        nameGroup.appendChild(nameInput);

        const emailGroup = document.createElement('div');
        emailGroup.className = 'public-form-group';
        emailGroup.id = 'contact-email-group';
        const emailLabel = document.createElement('label');
        emailLabel.className = 'public-form-label';
        emailLabel.htmlFor = 'contact-email';
        emailLabel.textContent = 'Tu email (para recibir respuesta)';
        const emailInput = document.createElement('input');
        emailInput.type = 'email';
        emailInput.id = 'contact-email';
        emailInput.className = 'public-form-input';
        emailInput.placeholder = 'tu@email.com';
        emailInput.required = true;
        emailGroup.appendChild(emailLabel);
        emailGroup.appendChild(emailInput);

        const phoneGroup = document.createElement('div');
        phoneGroup.className = 'public-form-group';
        phoneGroup.id = 'contact-phone-group';
        const phoneLabel = document.createElement('label');
        phoneLabel.className = 'public-form-label';
        phoneLabel.htmlFor = 'contact-phone';
        phoneLabel.textContent = 'Tu teléfono (opcional)';
        const phoneInput = document.createElement('input');
        phoneInput.type = 'tel';
        phoneInput.id = 'contact-phone';
        phoneInput.className = 'public-form-input';
        phoneInput.placeholder = '+57 300 123 4567';
        phoneGroup.appendChild(phoneLabel);
        phoneGroup.appendChild(phoneInput);

        const msgGroup = document.createElement('div');
        msgGroup.className = 'public-form-group';
        const msgLabel = document.createElement('label');
        msgLabel.className = 'public-form-label';
        msgLabel.htmlFor = 'message-content';
        msgLabel.textContent = 'Mensaje';
        const msgTextarea = document.createElement('textarea');
        msgTextarea.id = 'message-content';
        msgTextarea.className = 'public-form-textarea';
        msgTextarea.placeholder = 'Escribe tu mensaje aquí...';
        msgTextarea.required = true;
        msgGroup.appendChild(msgLabel);
        msgGroup.appendChild(msgTextarea);

        const actions = document.createElement('div');
        actions.className = 'public-message-actions';
        const cancelBtn = document.createElement('button');
        cancelBtn.type = 'button';
        cancelBtn.className = 'public-btn-cancel';
        cancelBtn.textContent = 'Cancelar';
        const sendBtn = document.createElement('button');
        sendBtn.type = 'submit';
        sendBtn.className = 'public-btn-send';
        const sendIcon = document.createElement('i');
        sendIcon.className = 'fas fa-paper-plane';
        sendBtn.appendChild(sendIcon);
        sendBtn.appendChild(document.createTextNode('Enviar mensaje'));
        actions.appendChild(cancelBtn);
        actions.appendChild(sendBtn);

        form.appendChild(bizInfo);
        form.appendChild(nameGroup);
        form.appendChild(emailGroup);
        form.appendChild(phoneGroup);
        form.appendChild(msgGroup);
        form.appendChild(actions);

        content.appendChild(header);
        content.appendChild(form);
        modal.appendChild(content);

        document.body.appendChild(modal);
        this.modal = modal;
    }

    // Vincular eventos
    bindEvents() {
        // Cerrar modal
        const closeBtn = this.modal.querySelector('.public-message-close');
        const cancelBtn = this.modal.querySelector('.public-btn-cancel');
        
        closeBtn.addEventListener('click', () => this.closeModal());
        cancelBtn.addEventListener('click', () => this.closeModal());
        
        // Cerrar al hacer clic fuera del modal
        this.modal.addEventListener('click', (e) => {
            if (e.target === this.modal) {
                this.closeModal();
            }
        });
        
        // Enviar formulario
        const form = this.modal.querySelector('#public-message-form');
        form.addEventListener('submit', (e) => this.handleSubmit(e));
        
        // Escape key para cerrar
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.modal.classList.contains('active')) {
                this.closeModal();
            }
        });
    }

    // Abrir modal para escribir a un negocio
    async openMessageModal(businessId, businessName, businessLogo = null) {
        this.currentBusiness = {
            id: businessId,
            name: businessName,
            logo: businessLogo
        };
        
        // Actualizar información del negocio en el modal
        this.updateBusinessInfo();
        
        // Los campos de contacto siempre están visibles para usuarios no autenticados
        const nameGroup = this.modal.querySelector('#contact-name-group');
        const emailGroup = this.modal.querySelector('#contact-email-group');
        const phoneGroup = this.modal.querySelector('#contact-phone-group');
        
        if (!this.isAuthenticated) {
            if (nameGroup) nameGroup.style.display = 'block';
            if (emailGroup) emailGroup.style.display = 'block';
            if (phoneGroup) phoneGroup.style.display = 'block';
        } else {
            if (nameGroup) nameGroup.style.display = 'none';
            if (emailGroup) emailGroup.style.display = 'none';
            if (phoneGroup) phoneGroup.style.display = 'none';
        }
        
        // Limpiar formulario
        this.clearForm();
        
        // Mostrar modal
        this.modal.classList.add('active');
        document.body.classList.add('modal-open');
        
        // Focus en el primer campo
        setTimeout(() => {
            const firstField = this.modal.querySelector('#contact-name') || this.modal.querySelector('#message-content');
            if (firstField) firstField.focus();
        }, 100);
    }

    // Actualizar información del negocio en el modal
    updateBusinessInfo() {
        const businessInfo = this.modal.querySelector('#business-info');
        if (!businessInfo) return;
        const fallback = 'https://cdn.publikaya.com/assets/images/placeholder-banner.webp';
        const rawLogoUrl = (this.currentBusiness && this.currentBusiness.logo) ? String(this.currentBusiness.logo) : '';
        let logoUrl = fallback;
        try {
            const u = new URL(rawLogoUrl, window.location.origin);
            const p = u.protocol.toLowerCase();
            if (p === 'http:' || p === 'https:') logoUrl = u.href;
        } catch (_) {}

        businessInfo.replaceChildren();
        const img = document.createElement('img');
        img.className = 'public-business-logo';
        img.alt = 'Logo';
        img.src = logoUrl;
        img.addEventListener('error', () => { img.src = fallback; }, { once: true });

        const details = document.createElement('div');
        details.className = 'public-business-details';

        const h4 = document.createElement('h4');
        h4.textContent = (this.currentBusiness && this.currentBusiness.name) ? String(this.currentBusiness.name) : '';
        const p = document.createElement('p');
        p.textContent = 'Enviar mensaje directo';

        details.appendChild(h4);
        details.appendChild(p);
        businessInfo.appendChild(img);
        businessInfo.appendChild(details);
    }

    // Cerrar modal
    closeModal() {
        this.modal.classList.remove('active');
        document.body.classList.remove('modal-open');
        this.currentBusiness = null;
    }

    // Limpiar formulario
    clearForm() {
        const form = this.modal.querySelector('#public-message-form');
        form.reset();
        
        // Remover estados de error
        const inputs = form.querySelectorAll('.public-form-input, .public-form-textarea');
        inputs.forEach(input => {
            input.style.borderColor = '#e5e7eb';
        });
    }

    // Manejar envío del formulario
    async handleSubmit(e) {
        e.preventDefault();
        
        const submitBtn = this.modal.querySelector('.public-btn-send');
        const form = e.target;
        
        // Obtener datos del formulario
        let name = this.modal.querySelector('#contact-name')?.value.trim() || '';
        let email = this.modal.querySelector('#contact-email')?.value.trim() || '';
        let phone = this.modal.querySelector('#contact-phone')?.value.trim() || '';
        let message = this.modal.querySelector('#message-content')?.value.trim() || '';
        
        // Validación básica
        if (!name || !message) {
            this.showNotification('Por favor completa el nombre y mensaje', 'error');
            return;
        }
        
        if (!this.isAuthenticated && !email) {
            this.showNotification('Por favor ingresa tu email para recibir respuesta', 'error');
            return;
        }
        
        // Sanitizar inputs del mensaje público
        if (typeof window.Sanitizer !== 'undefined') {
          const sanitized = window.Sanitizer.sanitizeObject(
            { name, email, phone, message },
            { name: 'text', email: 'email', phone: 'phone', message: 'text' }
          );
          name = sanitized.name;
          email = sanitized.email;
          phone = sanitized.phone;
          message = sanitized.message;
        }
        
        // Mostrar estado de carga
        this.setLoadingState(submitBtn, true);
        
        try {
            const messageData = {
                business_id: this.currentBusiness.id,
                sender_name: name,
                sender_email: email,
                sender_phone: phone,
                message: message
            };
            
            let response;
            
            if (this.isAuthenticated) {
                // Usuario autenticado - usar API de mensajería
                response = await this.sendAuthenticatedMessage(messageData);
            } else {
                // Usuario no autenticado - usar endpoint público
                response = await this.sendPublicMessage(messageData);
            }
            
            if (response.success) {
                this.showNotification('¡Mensaje enviado correctamente!', 'success');
                this.closeModal();
                
                // Opcional: redirigir a login para continuar conversación
                if (!this.isAuthenticated) {
                    setTimeout(() => {
                        this.showNotification('Inicia sesión para ver la respuesta y continuar la conversación', 'info');
                    }, 2000);
                }
            } else {
                throw new Error(response.message || 'Error al enviar mensaje');
            }
            
        } catch (error) {
            console.error('Error sending message:', error);
            this.showNotification('Error al enviar el mensaje. Inténtalo de nuevo.', 'error');
        } finally {
            this.setLoadingState(submitBtn, false);
        }
    }

    // Enviar mensaje como usuario autenticado
    async sendAuthenticatedMessage(messageData) {
        const token = localStorage.getItem('supabase.auth.token') || 
                     sessionStorage.getItem('supabase.auth.token');
        
        const apiBaseURL = window.apiConfig?.baseURL || 'http://localhost:3000/api/v1';
        const response = await fetch(`${apiBaseURL}/conversations`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                type: 'user_business',
                participant_id: messageData.business_id,
                initial_message: {
                    content: `${messageData.subject}\n\n${messageData.content}`,
                    type: 'text'
                }
            })
        });
        
        if (!response.ok) {
            throw new Error('Failed to send authenticated message');
        }
        
        return await response.json();
    }

    // Enviar mensaje como usuario no autenticado
    async sendPublicMessage(messageData) {
        const apiBaseURL = window.apiConfig?.baseURL || 'http://localhost:3000/api/v1';
        const csrfToken = (() => {
            try {
                const m = document.cookie.match(/(?:^|; )csrf_token=([^;]+)/);
                return m ? decodeURIComponent(m[1]) : '';
            } catch (_) {
                return '';
            }
        })();

        const response = await fetch(`${apiBaseURL}/messaging/public/send`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                ...(csrfToken ? { 'x-csrf-token': csrfToken } : {})
            },
            body: JSON.stringify(messageData)
        });
        
        if (!response.ok) {
            let detail = '';
            try {
                const ct = response.headers.get('content-type') || '';
                if (ct.includes('application/json')) {
                    const data = await response.json();
                    detail = data?.message || data?.error || '';
                } else {
                    detail = await response.text();
                }
            } catch (_) {}
            throw new Error(detail || 'Failed to send public message');
        }
        
        return await response.json();
    }

    // Establecer estado de carga en botón
    setLoadingState(button, loading) {
        if (loading) {
            button.disabled = true;
            button.classList.add('loading');
            button.replaceChildren();
            const sp = document.createElement('div');
            sp.className = 'spinner';
            button.appendChild(sp);
            button.appendChild(document.createTextNode(' Enviando...'));
        } else {
            button.disabled = false;
            button.classList.remove('loading');
            button.replaceChildren();
            const i = document.createElement('i');
            i.className = 'fas fa-paper-plane';
            button.appendChild(i);
            button.appendChild(document.createTextNode(' Enviar mensaje'));
        }
    }

    // Mostrar notificación
    showNotification(message, type = 'info') {
        // Remover notificaciones existentes
        const existing = document.querySelectorAll('.public-notification');
        existing.forEach(n => n.remove());
        
        const notification = document.createElement('div');
        notification.className = `public-notification ${type}`;
        notification.textContent = message;
        
        document.body.appendChild(notification);
        
        // Auto-remover después de 5 segundos
        setTimeout(() => {
            if (notification.parentNode) {
                notification.remove();
            }
        }, 5000);
    }
}

// Función global para abrir modal desde las tarjetas
window.openBusinessMessage = function(businessId, businessName, businessLogo = null) {
    if (!window.publicMessaging) {
        window.publicMessaging = new PublicMessaging();
    }
    
    window.publicMessaging.openMessageModal(businessId, businessName, businessLogo);
};

// Inicializar cuando el DOM esté listo
document.addEventListener('DOMContentLoaded', () => {
    window.publicMessaging = new PublicMessaging();
});

// Exportar para uso en módulos
if (typeof module !== 'undefined' && module.exports) {
    module.exports = PublicMessaging;
}
